import { useTranslation } from 'react-i18next'
import { useParams, Navigate } from 'react-router-dom'
import { Calendar } from 'lucide-react'
import {
  getCategoryKeyFromTreatmentSlug,
  getCategoryKeyFromCategorySlug,
} from '@/lib/treatments'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'
import { PageSeo, Breadcrumbs, Button } from '@/components/ui'
import { LocalizedLink } from '@/components/LocalizedLink'
import {
  TreatmentHero,
  TreatmentInfoSection,
  TreatmentIntro,
  TreatmentListSection,
  TreatmentBenefitsSection,
  TreatmentTextBlock,
  TreatmentGallery,
  TreatmentCTA,
} from '@/components/treatment'
import type { BeforeAfterPair } from '@/components/treatment'

interface Section {
  type: 'techniques' | 'benefits' | 'text' | 'gallery'
  title?: string
  subtitle?: string
  items?: string[]
  paragraphs?: string[]
  images?: { src: string; alt?: string; caption?: string }[]
}

export function TreatmentPage() {
  const { categorySlug, treatmentSlug } = useParams<{
    categorySlug: string
    treatmentSlug: string
  }>()
  const { t, i18n } = useTranslation([treatmentSlug, 'common'])
  const lang = i18n.language as SupportedLanguage

  const categoryKey = getCategoryKeyFromCategorySlug(categorySlug || '', lang)
  const expectedCategoryKey = getCategoryKeyFromTreatmentSlug(
    treatmentSlug || '',
    lang
  )

  if (
    !categoryKey ||
    !expectedCategoryKey ||
    categoryKey !== expectedCategoryKey
  ) {
    return <Navigate to={`/${lang}`} replace />
  }

  const bundle = (
    i18n.getResourceBundle(lang, treatmentSlug || '') as Record<string, unknown> | undefined
  ) || {}

  const seo = (bundle.seo as { title?: string; description?: string } | undefined) || {
    title: t(`${treatmentSlug}:title`),
    description: t(`${treatmentSlug}:subtitle`),
  }
  const hero = (bundle.hero as {
    title?: string
    subtitle?: string
    image?: string
    imageAlt?: string
  } | undefined) || {
    title: t(`${treatmentSlug}:title`),
    subtitle: t(`${treatmentSlug}:subtitle`),
  }
  const info = (bundle.info as {
    duration?: string
    anesthesia?: string
    hospitalization?: string
    recovery?: string
    results?: string
  } | undefined) || {}
  const intro = (bundle.intro as {
    title?: string
    paragraphs?: string[]
    image?: string
    imageAlt?: string
    beforeAfter?: BeforeAfterPair[]
    beforeLabel?: string
    afterLabel?: string
    videoUrl?: string
  } | undefined)
  const sections = Array.isArray(bundle.sections) ? (bundle.sections as Section[]) : []

  const sectionBackgrounds = (() => {
    let step = 0
    return sections.map((section) => {
      if (section.type === 'benefits') return 'soft' as const
      const bg = step % 2 === 0 ? ('white' as const) : ('cream' as const)
      step += 1
      return bg
    })
  })()

  const cta = (bundle.cta as {
    title?: string
    subtitle?: string
    button?: string
  } | undefined) || {
    title: t('common:routes.bookConsultation'),
    button: t('common:buttons.bookConsultation'),
  }

  const categoryLabel = t(`common:routes.${categoryKey}`)
  const treatmentTitle = hero.title || t(`${treatmentSlug}:title`)

  const breadcrumbNavItems = [
    { label: t('common:routes.home'), to: 'home' as RouteKey },
    { label: t('common:nav.services') },
    { label: categoryLabel, to: categoryKey },
    { label: treatmentTitle },
  ]

  const breadcrumbSchemaItems = [
    { name: t('common:routes.home'), path: getLocalizedPathFromRouteKey('home', lang) },
    { name: t('common:nav.services') },
    { name: categoryLabel, path: getLocalizedPathFromRouteKey(categoryKey, lang) },
    { name: treatmentTitle },
  ]

  const canonicalUrl = typeof window !== 'undefined' ? window.location.href : ''
  const baseUrl = canonicalUrl.split('/').slice(0, 3).join('/')

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: seo.title || treatmentTitle,
      description: seo.description || hero.subtitle,
      url: canonicalUrl,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbSchemaItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.path ? `${baseUrl}${item.path}` : undefined,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalProcedure',
      name: treatmentTitle,
      description: seo.description || hero.subtitle,
      url: canonicalUrl,
      procedureType: treatmentTitle,
      performedBy: {
        '@type': 'Physician',
        name: "Dr. Alessandro Dall'Antonia",
        worksFor: { '@type': 'MedicalBusiness', name: 'AD Aesthetics' },
      },
    },
  ]

  return (
    <>
      <PageSeo
        title={seo.title || treatmentTitle}
        description={seo.description || ''}
        jsonLd={jsonLd}
        breadcrumb={breadcrumbSchemaItems}
      />

      <main>
        <TreatmentHero
          title={treatmentTitle}
          subtitle={hero.subtitle}
          image={hero.image}
          imageAlt={hero.imageAlt}
        />

        <Breadcrumbs items={breadcrumbNavItems} />

        {intro && intro.paragraphs && intro.paragraphs.length > 0 && (
          <TreatmentIntro
            title={intro.title}
            paragraphs={intro.paragraphs}
            image={intro.image}
            imageAlt={intro.imageAlt}
            beforeAfter={intro.beforeAfter}
            beforeLabel={intro.beforeLabel}
            afterLabel={intro.afterLabel}
            videoUrl={intro.videoUrl}
          />
        )}

        <TreatmentInfoSection
          duration={info.duration}
          anesthesia={info.anesthesia}
          hospitalization={info.hospitalization}
          recovery={info.recovery}
          results={info.results}
        />

        {sections.map((section, index) => {
          const background = sectionBackgrounds[index]
          switch (section.type) {
            case 'techniques':
              return (
                <TreatmentListSection
                  key={index}
                  title={section.title}
                  subtitle={section.subtitle}
                  items={section.items || []}
                  variant="bullet"
                  background={background}
                />
              )
            case 'benefits':
              return (
                <TreatmentBenefitsSection
                  key={index}
                  title={section.title}
                  subtitle={section.subtitle}
                  items={section.items || []}
                />
              )
            case 'text':
              return (
                <TreatmentTextBlock
                  key={index}
                  title={section.title}
                  paragraphs={section.paragraphs || []}
                  background={background}
                />
              )
            case 'gallery':
              return (
                <TreatmentGallery
                  key={index}
                  title={section.title}
                  subtitle={section.subtitle}
                  images={section.images || []}
                />
              )
            default:
              return null
          }
        })}

        {!cta.title && (
          <section className="bg-primary py-16 md:py-24">
            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
              <LocalizedLink to="bookConsultation">
                <Button variant="primary-light">
                  <Calendar className="w-4 h-4" />
                  {t('common:buttons.bookConsultation')}
                </Button>
              </LocalizedLink>
            </div>
          </section>
        )}

        {cta.title && (
          <TreatmentCTA
            title={cta.title}
            subtitle={cta.subtitle}
            button={cta.button}
          />
        )}
      </main>
    </>
  )
}

function getLocalizedPathFromRouteKey(
  routeKey: RouteKey,
  lang: SupportedLanguage
): string {
  const path = localizedRoutes[routeKey][lang]
  return path ? `/${lang}/${path}` : `/${lang}`
}
